import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { NovidadeTiItem, NovidadeTiVisualizacao } from '../../types';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import { Sparkles, Wrench, Megaphone, Loader2, Wand2, ExternalLink, Eye, Zap, ChevronDown } from 'lucide-react';
import { DEPARTAMENTOS } from '../../src/lib/departamentos';
import { departamentosAlvejamPerfil } from '../../src/lib/departamentosAlvejamPerfil';
import { agruparPorData, dataReferenciaBr } from '../../src/lib/agruparPorData';

// Painel de aprovação do feed de Novidades TI (ver src/components/
// NovidadesTiModal.tsx, a tela que o staff vê). Itens nascem 'rascunho' — pelo
// trigger em tempo real (disparar_novidade_ti, ao concluir a solicitação) ou
// pela rede de segurança noturna (gerar_itens_novidades_ti, pega o que o tempo
// real perder). O admin aprova por seleção múltipla de lotes (datas); se
// ninguém aprovar em 1 dia, auto_publicar_novidades_ti_pendentes publica
// sozinho — exceto o que estiver com o toggle "incluir_publicacao" desligado.

// Precisa bater com RECIPIENT_ROLES em src/components/AvisosSistemaGate.tsx —
// é quem enxerga o feed publicado, a base do "quantos já viram".
const RECIPIENT_ROLES = ['adm', 'gerente', 'analista', 'vendedor', 'gerente_parcerias'];

type ItemComPerfil = NovidadeTiItem & { profiles: { full_name: string } | null };
type ItemDoMes = Pick<NovidadeTiItem, 'categoria' | 'origem'>;
interface PerfilElegivel { id: string; full_name: string; departamento: string | null; departamentos: string[] | null; }

const CATEGORIA_CFG: Record<NovidadeTiItem['categoria'], { label: string; color: string; Icon: typeof Sparkles }> = {
    novidade: { label: 'Novidade', color: 'bg-primary/10 text-primary dark:bg-primary/20', Icon: Sparkles },
    correcao: { label: 'Correção', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', Icon: Wrench },
};

const dataHoraBr = (iso: string): string => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const MetricCard: React.FC<{ label: string; value: React.ReactNode; sub?: string }> = ({ label, value, sub }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
);

const ToggleInclusao: React.FC<{ incluido: boolean; carregando: boolean; onToggle: () => void }> = ({ incluido, carregando, onToggle }) => (
    <button
        onClick={onToggle}
        disabled={carregando}
        role="switch"
        aria-checked={incluido}
        title={incluido ? 'Incluído na publicação — clique para excluir (fica como rascunho, não é apagado)' : 'Excluído da publicação — clique para incluir de novo'}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-wait ${incluido ? 'bg-green-500' : 'bg-red-400'}`}
    >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${incluido ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
);

// Escolhido dentro do modal de publicação (não por item) — vale pra todos os
// lotes selecionados de uma vez. selecionadas=[] é o sentinela de "todo o
// staff" (mesma semântica de departamentos_alvo null no banco).
const SeletorEquipes: React.FC<{ selecionadas: string[]; onChange: (novas: string[]) => void }> = ({ selecionadas, onChange }) => {
    const todoStaff = selecionadas.length === 0;
    return (
        <div className="mt-4 text-left">
            <label className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                <input type="checkbox" checked={todoStaff} onChange={() => onChange([])} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                Todo o staff
            </label>
            <div className={`grid grid-cols-2 gap-x-4 gap-y-1.5 pl-1 transition-opacity ${todoStaff ? 'opacity-60' : ''}`}>
                {DEPARTAMENTOS.map((d) => (
                    <label key={d} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <input
                            type="checkbox"
                            checked={selecionadas.includes(d)}
                            onChange={() => onChange(selecionadas.includes(d) ? selecionadas.filter((s) => s !== d) : [...selecionadas, d])}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        {d}
                    </label>
                ))}
            </div>
        </div>
    );
};

const NovidadesTiView: React.FC = () => {
    const { profile } = useAuth();
    const { addToast } = useToast();
    const souAdm = profile?.role === 'adm';

    const [pendentes, setPendentes] = useState<ItemComPerfil[]>([]);
    const [publicados, setPublicados] = useState<ItemComPerfil[]>([]);
    const [itensMes, setItensMes] = useState<ItemDoMes[]>([]);
    const [visualizacoes, setVisualizacoes] = useState<NovidadeTiVisualizacao[]>([]);
    const [perfisElegiveis, setPerfisElegiveis] = useState<PerfilElegivel[]>([]);
    const [loading, setLoading] = useState(true);
    const [lotesSelecionados, setLotesSelecionados] = useState<Set<string>>(new Set());
    const [publicandoSelecionados, setPublicandoSelecionados] = useState(false);
    const [confirmarPublicacao, setConfirmarPublicacao] = useState(false);
    const [equipesSelecionadas, setEquipesSelecionadas] = useState<string[]>([]);
    const [alternandoId, setAlternandoId] = useState<string | null>(null);
    const [gerandoDescricoes, setGerandoDescricoes] = useState(false);
    const [lotesExpandidos, setLotesExpandidos] = useState<Set<string>>(new Set());

    const carregar = async () => {
        setLoading(true);
        try {
            const inicioMes = new Date();
            inicioMes.setDate(1);
            inicioMes.setHours(0, 0, 0, 0);

            const [
                { data: pend, error: errPend },
                { data: pub, error: errPub },
                { data: mesData, error: errMes },
                { data: vis, error: errVis },
                { data: perfis, error: errElegiveis },
            ] = await Promise.all([
                supabase.from('novidades_ti_itens').select('*, profiles(full_name)').eq('status', 'rascunho').order('data_referencia', { ascending: false }).order('concluido_em', { ascending: true }),
                supabase.from('novidades_ti_itens').select('*, profiles(full_name)').eq('status', 'publicado').order('concluido_em', { ascending: false }).limit(50),
                supabase.from('novidades_ti_itens').select('categoria, origem').gte('criado_em', inicioMes.toISOString()),
                supabase.from('novidades_ti_visualizacoes').select('profile_id, ultima_visualizacao_em'),
                supabase.from('profiles').select('id, full_name, departamento, departamentos').in('role', RECIPIENT_ROLES),
            ]);
            if (errPend) throw errPend;
            if (errPub) throw errPub;
            if (errMes) throw errMes;
            if (errVis) throw errVis;
            if (errElegiveis) throw errElegiveis;
            setPendentes((pend || []) as unknown as ItemComPerfil[]);
            setPublicados((pub || []) as unknown as ItemComPerfil[]);
            setItensMes((mesData || []) as ItemDoMes[]);
            setVisualizacoes((vis || []) as NovidadeTiVisualizacao[]);
            setPerfisElegiveis((perfis || []) as PerfilElegivel[]);
            setLotesSelecionados(new Set());
        } catch (err) {
            console.error('Erro ao carregar novidades TI:', err);
            addToast('Erro ao carregar novidades TI.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { carregar(); }, []);

    const lotesPendentes = useMemo(() => agruparPorData(pendentes), [pendentes]);
    const lotesPublicados = useMemo(() => agruparPorData(publicados), [publicados]);
    const temItemSemDescricaoIA = useMemo(() => pendentes.some((item) => !item.descricao), [pendentes]);

    const metricas = useMemo(() => {
        const total = itensMes.length;
        const correcao = itensMes.filter((i) => i.categoria === 'correcao').length;
        const tempoReal = itensMes.filter((i) => i.origem === 'tempo_real').length;
        return { total, correcao, novidade: total - correcao, tempoReal, loteNoturno: total - tempoReal };
    }, [itensMes]);

    // Um lote inteiro nasce de UM clique em "Publicar selecionados", então todo
    // mundo nele compartilha o mesmo alvo — usar o do primeiro item é seguro na
    // prática (só divergiria se o mesmo dia recebesse duas publicações
    // separadas com alvos diferentes, caso raro que não vale a complexidade de
    // tratar por item).
    //
    // Diferente de Avisos do Sistema (uma linha de confirmação por aviso, por
    // pessoa — visualizado_em + confirmado_em separados), Novidades TI guarda
    // só um CURSOR por pessoa (novidades_ti_visualizacoes.ultima_visualizacao_em,
    // sempre sobrescrito no "Li e aceito") — não dá pra saber se ela viu ESSE
    // item especificamente, só se o cursor dela já passou da data de corte
    // deste lote. Por isso a tabela abaixo tem uma coluna a menos (só
    // "confirmou em", sem "visualizou em" separado — aqui as duas coisas
    // acontecem juntas, no mesmo clique).
    const detalhesPorLote = (itens: ItemComPerfil[]) => {
        if (itens.length === 0) return { vistos: 0, total: 0, alvo: null as string[] | null, colaboradores: [] as { id: string; nome: string; confirmouEm: string | null }[] };
        const alvo = itens[0].departamentos_alvo ?? null;
        const elegiveis = perfisElegiveis.filter((p) => departamentosAlvejamPerfil(alvo, p));
        // publicado_em (não concluido_em): é o que o cursor de "já visto" do
        // Gate realmente compara — usar concluido_em aqui sub-contaria "já viu"
        // pra item rascunho há dias e só publicado agora (bug real encontrado
        // testando o alvo por equipe, corrigido junto com o Gate).
        const dataParaCorte = (i: ItemComPerfil) => i.publicado_em ?? i.concluido_em;
        const maisRecente = itens.reduce((max, i) => (dataParaCorte(i) > max ? dataParaCorte(i) : max), dataParaCorte(itens[0]));
        const cursorPorId = new Map(visualizacoes.map((v) => [v.profile_id, v.ultima_visualizacao_em]));
        const colaboradores = elegiveis
            .map((p) => {
                const cursor = cursorPorId.get(p.id);
                const confirmouEm = cursor && cursor >= maisRecente ? cursor : null;
                return { id: p.id, nome: p.full_name, confirmouEm };
            })
            .sort((a, b) => {
                if (!!a.confirmouEm !== !!b.confirmouEm) return a.confirmouEm ? -1 : 1;
                if (a.confirmouEm && b.confirmouEm) return b.confirmouEm.localeCompare(a.confirmouEm);
                return a.nome.localeCompare(b.nome);
            });
        const vistos = colaboradores.filter((c) => c.confirmouEm).length;
        return { vistos, total: elegiveis.length, alvo, colaboradores };
    };

    const alternarExpansaoLote = (dataReferencia: string) => {
        setLotesExpandidos((prev) => {
            const novo = new Set(prev);
            if (novo.has(dataReferencia)) novo.delete(dataReferencia);
            else novo.add(dataReferencia);
            return novo;
        });
    };

    const alternarSelecaoLote = (dataReferencia: string) => {
        setLotesSelecionados((prev) => {
            const novo = new Set(prev);
            if (novo.has(dataReferencia)) novo.delete(dataReferencia);
            else novo.add(dataReferencia);
            return novo;
        });
    };

    const publicarSelecionados = async () => {
        setPublicandoSelecionados(true);
        try {
            const { error } = await supabase
                .from('novidades_ti_itens')
                .update({ status: 'publicado', departamentos_alvo: equipesSelecionadas.length > 0 ? equipesSelecionadas : null, publicado_em: new Date().toISOString() })
                .in('data_referencia', Array.from(lotesSelecionados))
                .eq('status', 'rascunho')
                .eq('incluir_publicacao', true);
            if (error) throw error;
            addToast(`${lotesSelecionados.size} lote(s) publicado(s).`, 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'publicar_novidades_ti', origem: 'acao' });
            await carregar();
        } catch (err) {
            console.error('Erro ao publicar lotes selecionados:', err);
            addToast('Erro ao publicar os lotes selecionados.', 'error');
        } finally {
            setPublicandoSelecionados(false);
            setConfirmarPublicacao(false);
        }
    };

    const alternarInclusao = async (item: ItemComPerfil) => {
        setAlternandoId(item.id);
        const novoValor = !item.incluir_publicacao;
        try {
            const { error } = await supabase.from('novidades_ti_itens').update({ incluir_publicacao: novoValor }).eq('id', item.id);
            if (error) throw error;
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'alternar_inclusao_novidade', origem: 'acao' });
            setPendentes((prev) => prev.map((i) => (i.id === item.id ? { ...i, incluir_publicacao: novoValor } : i)));
        } catch (err) {
            console.error('Erro ao alternar inclusão do item:', err);
            addToast('Não foi possível atualizar esse item agora.', 'error');
        } finally {
            setAlternandoId(null);
        }
    };

    const gerarDescricoesPendentes = async () => {
        setGerandoDescricoes(true);
        try {
            const { data, error } = await supabase.functions.invoke('gerar-descricao-novidade-ti');
            if (error) throw error;
            const falhas = data?.data?.falhas ?? 0;
            if (falhas > 0) addToast(`${falhas} descrição(ões) não puderam ser geradas — tente de novo em alguns minutos.`, 'warning');
            else addToast('Descrições geradas pela Vick.', 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'gerar_descricoes_novidades_ia', origem: 'acao' });
            await carregar();
        } catch (err) {
            console.error('Erro ao gerar descrições com IA:', err);
            addToast('Não foi possível gerar as descrições agora.', 'error');
        } finally {
            setGerandoDescricoes(false);
        }
    };

    if (loading) return <LoadingSpinner label="Carregando novidades TI..." />;

    const renderItem = (item: ItemComPerfil, podeAlternar: boolean) => {
        const cfg = CATEGORIA_CFG[item.categoria];
        return (
            <li key={item.id} className={`flex items-center justify-between gap-3 px-5 py-3 transition-opacity ${!item.incluir_publicacao ? 'opacity-50' : ''}`}>
                <div className="min-w-0 flex-1">
                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${cfg.color}`}>
                        <cfg.Icon size={11} />
                        {cfg.label}
                    </span>
                    <a
                        href={`#/tarefas/solicitacoes?id=${item.tarefa_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm font-medium text-gray-900 dark:text-white mt-1 hover:underline w-fit max-w-full"
                        title={item.titulo_original ? `Título original do chamado: ${item.titulo_original} — clique pra abrir a solicitação` : 'Ainda não reescrito pela Vick — clique pra abrir a solicitação original'}
                    >
                        <span className="truncate">{item.titulo}</span>
                        <ExternalLink size={11} className="text-gray-300 shrink-0" />
                        {!item.descricao && <span className="ml-0.5 shrink-0 text-[10px] font-bold uppercase text-gray-300 dark:text-gray-600">(sem detalhamento)</span>}
                    </a>
                    {item.descricao && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.descricao}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                        {item.tag && `${item.tag} · `}
                        {item.profiles?.full_name || 'alguém do time'} · {dataHoraBr(item.concluido_em)}
                    </p>
                </div>
                {podeAlternar && (
                    <ToggleInclusao incluido={item.incluir_publicacao} carregando={alternandoId === item.id} onToggle={() => alternarInclusao(item)} />
                )}
            </li>
        );
    };

    return (
        <div className="space-y-8 animate-fade-in">
            <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Megaphone size={22} className="text-primary" />
                    Novidades TI
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Feed persistente gerado a partir da Central de Solicitações — aprove por lote antes de entrar no ar pro staff.
                </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard label="Itens este mês" value={metricas.total} />
                <MetricCard label="Novidade / Correção" value={`${metricas.novidade} / ${metricas.correcao}`} />
                <MetricCard
                    label="Tempo real"
                    value={<span className="flex items-center gap-1.5"><Zap size={16} className="text-primary" />{metricas.tempoReal}</span>}
                    sub={`${metricas.loteNoturno} pela rede de segurança noturna`}
                />
                <MetricCard
                    label="Lotes pendentes"
                    value={lotesPendentes.length}
                    sub={lotesPendentes.length > 0 ? 'Auto-publica em 1 dia se ninguém revisar' : undefined}
                />
            </div>

            <div>
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Lotes pendentes de aprovação</h3>
                    <div className="flex items-center gap-4">
                        {souAdm && temItemSemDescricaoIA && (
                            <button
                                onClick={gerarDescricoesPendentes}
                                disabled={gerandoDescricoes}
                                title="A Vick reescreve o título técnico do chamado como uma frase curta, sem jargão — roda sozinha de madrugada, esse botão é só um atalho."
                                className="flex items-center gap-1.5 text-xs font-bold text-primary hover:text-primary/80 disabled:opacity-50 disabled:cursor-wait"
                            >
                                {gerandoDescricoes ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                                Gerar descrições com a Vick
                            </button>
                        )}
                        {souAdm && lotesSelecionados.size > 0 && (
                            <button
                                onClick={() => { setEquipesSelecionadas([]); setConfirmarPublicacao(true); }}
                                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs px-3 py-1.5 rounded-lg transition-colors"
                            >
                                Publicar selecionados ({lotesSelecionados.size})
                            </button>
                        )}
                    </div>
                </div>
                {lotesPendentes.length === 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-8 text-center text-sm text-gray-400">
                        Nenhum lote pendente. O próximo é gerado automaticamente na madrugada, se houver solicitações concluídas.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {lotesPendentes.map(([dataReferencia, itens]) => (
                            <div key={dataReferencia} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
                                    {souAdm && (
                                        <input
                                            type="checkbox"
                                            checked={lotesSelecionados.has(dataReferencia)}
                                            onChange={() => alternarSelecaoLote(dataReferencia)}
                                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary shrink-0"
                                            aria-label={`Selecionar lote de ${dataReferenciaBr(dataReferencia)} para publicar`}
                                        />
                                    )}
                                    <p className="text-sm font-bold text-gray-700 dark:text-gray-300 flex-1">
                                        {dataReferenciaBr(dataReferencia)} <span className="font-normal text-gray-400">— {itens.length} {itens.length === 1 ? 'item' : 'itens'}</span>
                                    </p>
                                </div>
                                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {itens.map((item) => renderItem(item, souAdm))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div>
                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Publicados recentemente</h3>
                {lotesPublicados.length === 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-8 text-center text-sm text-gray-400">
                        Nada publicado ainda.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {lotesPublicados.map(([dataReferencia, itens]) => {
                            const { vistos, total, alvo, colaboradores } = detalhesPorLote(itens);
                            const expandido = lotesExpandidos.has(dataReferencia);
                            return (
                                <div key={dataReferencia} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                    <div className="flex items-center justify-between gap-3 px-5 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700 flex-wrap">
                                        <button
                                            onClick={() => alternarExpansaoLote(dataReferencia)}
                                            className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-300"
                                        >
                                            <ChevronDown size={16} className={`text-gray-400 transition-transform ${expandido ? '' : '-rotate-90'}`} />
                                            {dataReferenciaBr(dataReferencia)} <span className="font-normal text-gray-400">— {itens.length} {itens.length === 1 ? 'item' : 'itens'}</span>
                                        </button>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-gray-400" title={alvo && alvo.length > 0 ? alvo.join(', ') : undefined}>
                                                Alvo: {alvo && alvo.length > 0 ? (alvo.length > 2 ? `${alvo.slice(0, 2).join(', ')} +${alvo.length - 2}` : alvo.join(', ')) : 'Todo o staff'}
                                            </span>
                                            <button
                                                onClick={() => alternarExpansaoLote(dataReferencia)}
                                                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-primary"
                                                title="Colaboradores elegíveis pro alvo desse lote que já confirmaram ter visto (ou algo mais recente) no feed — clique pra ver o detalhamento"
                                            >
                                                <Eye size={13} /> {vistos} de {total} já viram
                                            </button>
                                        </div>
                                    </div>
                                    <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {itens.map((item) => renderItem(item, false))}
                                    </ul>
                                    {expandido && (
                                        <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 px-5 py-4">
                                            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Confirmações por colaborador</p>
                                            {colaboradores.length === 0 ? (
                                                <p className="text-sm text-gray-400">Nenhum colaborador elegível pra esse alvo.</p>
                                            ) : (
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-sm">
                                                        <thead>
                                                            <tr className="text-left text-xs font-bold text-gray-400 uppercase tracking-wide">
                                                                <th className="pb-2 pr-4">Colaborador</th>
                                                                <th className="pb-2">Confirmou em</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                                            {colaboradores.map((c) => (
                                                                <tr key={c.id}>
                                                                    <td className="py-1.5 pr-4 text-gray-700 dark:text-gray-300">{c.nome}</td>
                                                                    <td className={`py-1.5 ${c.confirmouEm ? 'text-gray-500 dark:text-gray-400' : 'text-gray-300 dark:text-gray-600'}`}>
                                                                        {c.confirmouEm ? dataHoraBr(c.confirmouEm) : 'Ainda não confirmou'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={confirmarPublicacao}
                onClose={() => setConfirmarPublicacao(false)}
                onConfirm={publicarSelecionados}
                title={`Publicar ${lotesSelecionados.size} lote(s) selecionado(s)?`}
                description={
                    <div>
                        <p>Os itens desses dias entram no feed de Novidades TI. Quem já tiver visualizado o feed antes volta a ficar pendente até confirmar de novo. Itens com o toggle desligado ficam de fora.</p>
                        <SeletorEquipes selecionadas={equipesSelecionadas} onChange={setEquipesSelecionadas} />
                    </div>
                }
                confirmText="Publicar"
                variant="warning"
                isLoading={publicandoSelecionados}
            />
        </div>
    );
};

export default NovidadesTiView;
